import * as React from "react";
import { getNormalOpenCloseStatusForDay, getOpenCloseStatus } from "@grandvisionhq/locations";
import { useIntl } from "@grandvisionhq/state";
const openNowFilterFunction = (store) => {
  const { openingHours, specialOpeningHours } = store;
  return ["OPEN", "CLOSING"].includes(getOpenCloseStatus(openingHours, specialOpeningHours));
};
const openOnSundayFilterFunction = (store) => {
  const { openingHours } = store;
  const Sunday = 0;
  return ["OPEN", "CLOSING"].includes(getNormalOpenCloseStatusForDay(openingHours, Sunday));
};
const openOnTheWeekendFilterFunction = (store) => store.weekendOpen === true;
const wheelChairAccessibleFilterFunction = (store) => store.wheelchairAccessible === true;
const useDefaultStoreSearchFilters = (additionalFilters = {}) => {
  const { getLabel } = useIntl();
  const storeSearchFilters = React.useMemo(
    () => ({
      openNow: {
        label: getLabel("stores.component.storeFinder.filter.openNow"),
        active: false,
        filterFunction: openNowFilterFunction,
        order: 0
      },
      openOnTheWeekend: {
        label: getLabel("stores.component.storeFinder.filter.openOnTheWeekend"),
        active: false,
        filterFunction: openOnTheWeekendFilterFunction,
        order: 1
      },
      openOnSunday: {
        label: getLabel("stores.component.storeFinder.filter.openOnSunday"),
        active: false,
        filterFunction: openOnSundayFilterFunction,
        order: 2
      },
      wheelChairAccessible: {
        label: getLabel("stores.component.storeFinder.filter.wheelChairAccessible"),
        active: false,
        filterFunction: wheelChairAccessibleFilterFunction,
        order: 3
      },
      ...additionalFilters
    }),
    [getLabel, additionalFilters]
  );
  return storeSearchFilters;
};
const setLoadingState = (callback) => {
  callback(true);
  setTimeout(() => callback(false), 100);
};
const emptyObject = {};
const useStoreSearchFilters = (props) => {
  const [loading, setLoading] = React.useState(false);
  const [storeSearchFilters, setStoreSearchFilters] = React.useState(
    props.filters ?? emptyObject
  );
  const storeSearchFilterList = React.useMemo(() => {
    const entries = Object.entries(storeSearchFilters);
    return entries.sort(([, a], [, b]) => (a.order ?? entries.length) - (b.order ?? entries.length));
  }, [storeSearchFilters]);
  const hasActiveFilters = React.useMemo(
    () => storeSearchFilterList.some(([, filter]) => filter.active),
    [storeSearchFilterList]
  );
  const setFilterActivity = React.useCallback(
    (storeFilterName, active) => {
      setLoadingState(setLoading);
      const filter = storeSearchFilters[storeFilterName];
      setStoreSearchFilters({ ...storeSearchFilters, [storeFilterName]: { ...filter, active } });
    },
    [storeSearchFilters]
  );
  const activeFilters = React.useMemo(
    () => Object.keys(storeSearchFilters).filter((key) => storeSearchFilters[key]?.active === true),
    [storeSearchFilters]
  );
  const clearAllFilters = React.useCallback(() => {
    setLoadingState(setLoading);
    setStoreSearchFilters(
      Object.keys(storeSearchFilters).reduce(
        (acc, key) => {
          acc[key] = {
            ...storeSearchFilters[key],
            active: false
          };
          return acc;
        },
        { ...storeSearchFilters }
      )
    );
  }, [storeSearchFilters]);
  const applyActiveFilters = React.useCallback(
    (storeSearchResults) => {
      if (!hasActiveFilters) {
        return storeSearchResults;
      }
      return Object.values(storeSearchFilters).reduce((acc, filter) => {
        if (!filter.active) {
          return acc;
        }
        return acc.filter(filter.filterFunction);
      }, storeSearchResults);
    },
    [hasActiveFilters, storeSearchFilters]
  );
  React.useEffect(() => {
    setStoreSearchFilters(props.filters ?? emptyObject);
  }, [props.filters]);
  return React.useMemo(
    () => [
      { setFilterActivity, applyActiveFilters, clearAllFilters },
      { storeSearchFilters, storeSearchFilterList, hasActiveFilters, loading, activeFilters }
    ],
    [
      setFilterActivity,
      applyActiveFilters,
      clearAllFilters,
      loading,
      storeSearchFilters,
      storeSearchFilterList,
      hasActiveFilters,
      activeFilters
    ]
  );
};
export {
  useStoreSearchFilters as default,
  useDefaultStoreSearchFilters,
  useStoreSearchFilters
};
//# sourceMappingURL=use-store-search-filters.js.map
